import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, Toaster } from "sonner";
import { ArrowRight, ArrowLeft, Check, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackEvent, getAttribution } from "@/lib/analytics";

const WINDOWS = ["All windows around", "Front two only", "Everything except front", "Not sure — I need advice"];
const SHADE = ["Light legal (~35% VLT)", "Medium (~25–20% VLT)", "Dark legal", "Not sure — walk me through it"];
const TIMELINE = ["ASAP", "Within 2 weeks", "1–2 months"];

const STEPS = ["Project", "Vehicle", "Contact"];

function Radio({ opt, active, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${active ? "border-[var(--red)] bg-red-50/60" : "border-neutral-200 hover:border-neutral-400"}`}
    >
      <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 grid place-items-center ${active ? "border-[var(--red)] bg-[var(--red)]" : "border-neutral-300"}`}>
        {active && <span className="w-2 h-2 rounded-full bg-white" />}
      </span>
      <span className={`text-sm ${active ? "text-neutral-900 font-semibold" : "text-neutral-700"}`}>{opt}</span>
    </button>
  );
}

export default function AutomotiveTintForm({ bare = false, source = "Auto Tint Service Page" }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    vehicle: "", windows: "", shade: "", timeline: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const step0Ready = form.windows && form.shade;
  const step1Ready = form.vehicle.trim();
  const step2Ready = form.full_name.trim() && form.email.trim() && form.phone.trim();

  const next = () => {
    const nextStep = step + 1;
    trackEvent("service_form_step_view", { service: "automotive-window-tint", step: nextStep + 1, step_name: STEPS[nextStep] });
    setStep(nextStep);
  };
  const back = () => setStep(Math.max(0, step - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!step2Ready || loading) return;
    setLoading(true);
    try {
      const details = [
        `Which windows?\n  → ${form.windows}`,
        `Shade preference\n  → ${form.shade}`,
        form.timeline && `Timeline\n  → ${form.timeline}`,
      ].filter(Boolean).join("\n\n");

      await api.post("/leads", {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        project_type: "Automotive Window Tint",
        vehicle_type: form.vehicle.trim(),
        project_details: details,
        referral_source: source,
        ...getAttribution(),
      });
      toast.success("Quote request received!");
      trackEvent("service_form_submit", { service: "automotive-window-tint" });
      const submissionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      navigate("/mockup/thank-you", {
        state: {
          fromSubmit: true,
          submissionId,
          service: "Automotive Window Tint",
          vehicle: form.vehicle.trim(),
          has_business: false,
          source,
        },
        replace: true,
      });
    } catch (err) {
      trackEvent("service_form_error", { service: "automotive-window-tint", message: formatApiErrorDetail(err.response?.data?.detail) });
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="auto-tint-form"
      className={bare ? "" : "bg-white rounded-3xl border border-neutral-200 p-6 md:p-8 shadow-xl"}
    >
      {!bare && <Toaster richColors position="top-right" />}

      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1 flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold border transition-colors ${i <= step ? "bg-[var(--red)] text-white border-[var(--red)]" : "bg-white text-neutral-500 border-neutral-200"}`}>
              {i < step ? <Check size={12}/> : i + 1}
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-[2px] bg-neutral-200 overflow-hidden">
                <div className="h-full bg-[var(--red)] transition-all duration-300" style={{ width: i < step ? "100%" : "0" }}/>
              </div>
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* STEP 1 — Windows + Shade (low-friction taps) */}
        {step === 0 && (
          <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="eyebrow eyebrow-red">Step 1 of 3</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">What are we tinting?</h3>

            <div className="mt-5">
              <label className="eyebrow">Which windows? *</label>
              <div className="mt-2 space-y-2">
                {WINDOWS.map((opt) => (
                  <Radio
                    key={opt}
                    opt={opt}
                    active={form.windows === opt}
                    onClick={() => setForm({ ...form, windows: opt })}
                    testid={`at-windows-${form.windows === opt ? "active" : "opt"}-${opt.slice(0,8).replace(/[^a-z]/gi,"").toLowerCase()}`}
                  />
                ))}
              </div>
            </div>

            <div className="mt-5">
              <label className="eyebrow">Shade preference *</label>
              <div className="mt-2 space-y-2">
                {SHADE.map((opt) => (
                  <Radio
                    key={opt}
                    opt={opt}
                    active={form.shade === opt}
                    onClick={() => setForm({ ...form, shade: opt })}
                    testid={`at-shade-${form.shade === opt ? "active" : "opt"}-${opt.slice(0,8).replace(/[^a-z]/gi,"").toLowerCase()}`}
                  />
                ))}
              </div>
            </div>

            <button
              type="button"
              data-testid="at-next-1"
              disabled={!step0Ready}
              onClick={next}
              className={`btn-red w-full justify-center mt-6 ${!step0Ready ? "opacity-60 pointer-events-none" : ""}`}
            >
              Continue <ArrowRight size={16}/>
            </button>
          </motion.div>
        )}

        {/* STEP 2 — Vehicle + optional timeline */}
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="eyebrow eyebrow-red">Step 2 of 3</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">Which vehicle?</h3>
            <p className="text-sm text-neutral-500 mt-1">Year, make and model helps Dave price it accurately.</p>

            <div className="mt-5 space-y-3">
              <input
                data-testid="at-vehicle"
                required
                placeholder="e.g. 2023 Toyota Tundra *"
                className="input-928"
                value={form.vehicle}
                onChange={set("vehicle")}
              />
              <div>
                <label className="eyebrow">Timeline (optional)</label>
                <select data-testid="at-timeline" className="input-928 mt-2" value={form.timeline} onChange={set("timeline")}>
                  <option value="">Select…</option>
                  {TIMELINE.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button type="button" data-testid="at-back-1" onClick={back} className="btn-ghost"><ArrowLeft size={16}/> Back</button>
              <button
                type="button"
                data-testid="at-next-2"
                disabled={!step1Ready}
                onClick={next}
                className={`btn-red ${!step1Ready ? "opacity-60 pointer-events-none" : ""}`}
              >
                Continue <ArrowRight size={16}/>
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 3 — Contact (the closer) */}
        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="eyebrow eyebrow-red">Step 3 of 3 · Last step</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">Where should Dave send the quote?</h3>
            <p className="text-sm text-neutral-500 mt-1">He&apos;ll text or email within 1 business day.</p>

            <div className="mt-5 space-y-3">
              <input data-testid="at-name" required placeholder="Full name *" className="input-928" value={form.full_name} onChange={set("full_name")} />
              <input data-testid="at-email" required type="email" placeholder="Email *" className="input-928" value={form.email} onChange={set("email")} />
              <input data-testid="at-phone" required type="tel" placeholder="Phone *" className="input-928" value={form.phone} onChange={set("phone")} />
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button type="button" data-testid="at-back-2" onClick={back} className="btn-ghost"><ArrowLeft size={16}/> Back</button>
              <button
                type="submit"
                data-testid="at-submit"
                disabled={loading || !step2Ready}
                className={`btn-red flex-1 justify-center whitespace-nowrap ${loading || !step2Ready ? "opacity-60 pointer-events-none" : ""}`}
              >
                {loading ? "Sending…" : "Send my request"}
                <ArrowRight size={16}/>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-6 text-xs text-neutral-500 text-center flex items-center justify-center gap-1.5"><ShieldCheck size={12}/> We never share your info · Dave replies within 1 business day</p>
    </form>
  );
}
